import { AiRun, AiRunRealtimeEvent, AiRunStatus, AiRunStep } from '../models/ai.model';

export const FINISHED_RUN: AiRunStatus[] = ['completed', 'failed', 'cancelled'];
const FINISHED_STEP = ['completed', 'failed', 'timeout', 'cancelled', 'returned'];

/**
 * Chamado 0000579137 — o aviso mais velho que o já aplicado (callback fora de ordem) é descartado.
 * Aviso sem `sequence` (`run.created`, `run.reconciled`) sempre vale. Registra a sequência aplicada.
 */
export function shouldApplyRealtimeEvent(
  lastSequenceByRun: Map<string, number>,
  event: AiRunRealtimeEvent
): boolean {
  if (typeof event.sequence !== 'number') {
    return true;
  }
  const last = lastSequenceByRun.get(event.run.id);
  if (last !== undefined && event.sequence <= last) {
    return false;
  }
  lastSequenceByRun.set(event.run.id, event.sequence);
  return true;
}

/**
 * Aplica no run só o que o aviso traz (status, datas, devoluções, tokens). Execução já finalizada
 * não volta atrás. `output`, `error` e `output_data` ficam como estão: vêm pela API.
 */
export function applyRunEvent<T extends AiRun>(run: T, event: AiRunRealtimeEvent): T {
  if (FINISHED_RUN.includes(run.status) && !FINISHED_RUN.includes(event.run.status)) {
    return run;
  }
  const info = event.run;
  return {
    ...run,
    status: info.status,
    started_at: info.started_at ?? run.started_at ?? null,
    finished_at: info.finished_at ?? run.finished_at ?? null,
    step_returns: info.step_returns ?? run.step_returns ?? 0,
    usage:
      typeof info.tokens === 'number' ? { ...run.usage, total_tokens: info.tokens } : run.usage,
  };
}

/**
 * Funde o step do aviso na lista, pela chave (step_order, iteration, attempt) — o `id` pode ainda
 * não existir na tela. Step novo entra com `created_at` do aviso (a timeline ordena por ele); step
 * já finalizado não regride.
 */
export function applyStepEvent(
  steps: readonly AiRunStep[] | null | undefined,
  event: AiRunRealtimeEvent
): AiRunStep[] {
  const current = [...(steps ?? [])];
  const info = event.step;
  if (!info) {
    return current;
  }

  const index = current.findIndex(
    step =>
      step.step_order === info.step_order &&
      (step.iteration ?? 1) === info.iteration &&
      (step.attempt ?? 1) === info.attempt
  );

  if (index >= 0) {
    const existing = current[index];
    if (FINISHED_STEP.includes(existing.status as string) && !FINISHED_STEP.includes(info.status)) {
      return current;
    }
    current[index] = {
      ...existing,
      id: existing.id ?? info.id,
      status: info.status as AiRunStatus,
      duration: info.duration ?? existing.duration ?? null,
      usage:
        typeof info.tokens === 'number'
          ? { ...existing.usage, total_tokens: info.tokens }
          : existing.usage,
      returned_to_step_order:
        info.returned_to_step_order ?? existing.returned_to_step_order ?? null,
    };
    return current;
  }

  current.push({
    id: info.id,
    step_order: info.step_order,
    step_name: info.step_name,
    iteration: info.iteration,
    attempt: info.attempt,
    status: info.status as AiRunStatus,
    duration: info.duration ?? null,
    usage: typeof info.tokens === 'number' ? { total_tokens: info.tokens } : null,
    returned_to_step_order: info.returned_to_step_order ?? null,
    created_at: event.sent_at ?? new Date().toISOString(),
  });
  return current;
}

/** Eventos que mudam o que só a API entrega (saída, erro, resultado): o detalhe é buscado de novo. */
export function shouldRefetchRunDetail(event: AiRunRealtimeEvent): boolean {
  return ['step.finished', 'run.finished', 'run.reconciled'].includes(event.event);
}
